import { Dot } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import DocumentViewer from "./DocumentViewer";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../context/AuthContext";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import PDF from "./svg/Pdf";
import AsyncSelect from "react-select/async";
import dateIcon from "../../../../assets/images/dateIcon.svg";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import EditingRestrictionNotice from "./EditRestrictionNotice";
import { documentValidation } from "../employeesModals/validation";

const HealthInsuranceNew = ({
  healthInsuranceDetails,
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const [fileNames, setFileNames] = useState({
    frontDocument: healthInsuranceDetails?.frontDocument || "",
    backDocument: healthInsuranceDetails?.backDocument || "",
  });
  const [healthInsuranceError, setHealthInsuranceError] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [editingSections, setEditingSections] = useState({
    healthInsurance: false,
    documents: false,
  });
  const [loading, setLoading] = useState(false);
  const { fileUpload, updateEmployeeRegistrationMaster, updateInsuranceInfo } =
    MainServices();

  const handleDocumentChange = (e, setFieldValue) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const file = files[0];

      // 1. Update Formik field name accordingly
      const fieldName =
        name === "frontDocument" ? "insuranceDocFront" : "insuranceDocBack";
      setFieldValue(fieldName, file); // Set the actual file in Formik

      // 2. Update your local file name state (for UI display)
      setFileNames((prev) => ({
        ...prev,
        [name]: {
          file: file,
          fileName: file.name,
        },
      }));

      console.log("File uploaded:", file.name);
    } else {
      console.log("No file selected");
    }
  };

  const handleRemoveDocument = (documentType, setFieldValue) => {
    setFileNames((prev) => ({
      ...prev,
      [documentType]: "", // Clear the document value
    }));

    // Clear the file input value if the document is removed
    if (documentType === "frontDocument") {
      fileInputRef1.current.value = ""; // Clear the file input
    } else if (documentType === "backDocument") {
      fileInputRef2.current.value = ""; // Clear the file input
    }
    // Update Formik field value
    const fieldName =
      documentType === "frontDocument"
        ? "insuranceDocFront"
        : "insuranceDocBack";
    setFieldValue(fieldName, "");
  };

  const handleViewDocument = (documentUrl, documentType, documentLabel) => {
    setSelectedDocument({
      url: documentUrl,
      type: getFileType(documentUrl),
      label: documentLabel,
    });
    setDocumentViewerOpen(true);
  };

  const getFileType = (url) => {
    try {
      const urlObj = new URL(url); // Ensure it's a valid URL
      const pathname = urlObj.pathname;
      const extension = pathname.split(".").pop().toLowerCase();

      return extension || "unknown"; // Return "unknown" if no extension found
    } catch (error) {
      return "invalid URL"; // Handle invalid URLs
    }
  };

  const handleSubmit = async (values) => {
    const updatedHealthInsuranceInfo = {
      ...(values?.insuranceClass && {
        insuranceClass: values.insuranceClass,
      }),
      ...(values?.insuranceNo && { insuranceNo: values.insuranceNo }),
      ...(values?.insuranceExpiry && {
        insuranceExpiry: values.insuranceExpiry,
      }),
    };

    setLoading(true);
    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };
    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      documents: {
        healthInsuranceInfo: updatedHealthInsuranceInfo,
      },
    };
    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        handleEditToggle("healthInsurance");
        setHealthInsuranceError(null);
        toast("Health Insurance Details Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update designation", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocuments = async () => {
    if (
      !healthInsuranceDetails?.insuranceNo ||
      !healthInsuranceDetails?.insuranceExpiry
    ) {
      setHealthInsuranceError(
        "Please complete all required Health Insurance details before submitting documents"
      );
      return;
    } else {
      setHealthInsuranceError(null); // Clear error if details are complete
    }
    const files = [];

    // Add files only if they exist
    if (fileNames?.frontDocument?.file) {
      files.push(fileNames.frontDocument.file);
    }
    if (fileNames?.backDocument?.file) {
      files.push(fileNames.backDocument.file);
    }

    try {
      let updatedFileNames = {};

      // If there are files to upload, call the fileUpload API
      if (files.length > 0) {
        const response = await fileUpload(files);
        if (response?.data) {
          const responseData = response?.data?.data;
          let currentIndex = 0;

          // Handle front document
          if (fileNames?.frontDocument?.file) {
            updatedFileNames.frontDocument =
              responseData[currentIndex]?.fileUrl;
            currentIndex++;
          } else {
            updatedFileNames.frontDocument = fileNames.frontDocument || ""; // Use existing value or empty
          }

          // Handle back document
          if (fileNames?.backDocument?.file) {
            updatedFileNames.backDocument = responseData[currentIndex]?.fileUrl;
          } else {
            updatedFileNames.backDocument = fileNames.backDocument || ""; // Use existing value or empty
          }
        } else {
          throw new Error(response?.message || "Unknown error");
        }
      } else {
        // If no files to upload, directly use the existing fileNames values
        updatedFileNames = {
          frontDocument: fileNames.frontDocument, // Use existing value or empty
          backDocument: fileNames.backDocument, // Use existing value or empty
        };
      }

      // Prepare the personal info object
      let formattedPersonalInfo = {
        ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
        ...(personalInfo?.secondName && {
          secondName: personalInfo.secondName,
        }),
        ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
        ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
        ...(personalInfo?.phone && { phone: personalInfo.phone }),
        ...(personalInfo?.email && { email: personalInfo.email }),
        ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
        ...(personalInfo?.dobGregorian && {
          dobGregorian: personalInfo.dobGregorian,
        }),
        ...(personalInfo?.religion && { religion: personalInfo.religion }),
        ...(personalInfo?.maritalStatus && {
          maritalStatus: personalInfo.maritalStatus,
        }),
        ...(personalInfo?.gender && { gender: personalInfo.gender }),
        ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

        ...(personalInfo?.citizen === "false" &&
          personalInfo?.nationality && {
            nationality: personalInfo?.nationality,
          }),
        ...(personalInfo?.citizen === "true" &&
          personalInfo?.nationalId && {
            nationalId: personalInfo.nationalId,
          }),
      };

      // Prepare the updated national ID info
      const updatedHealthInsuranceInfo = {
        frontDocument: updatedFileNames.frontDocument,
        backDocument: updatedFileNames.backDocument,
      };

      // Prepare the final payload
      const details = {
        ...(formattedPersonalInfo && formattedPersonalInfo),
        documents: {
          healthInsuranceInfo: updatedHealthInsuranceInfo,
        },
      };

      // Call the update API
      const updateResponse = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (updateResponse?.data) {
        toast("Health Insurance Documents Updated Successfully!", {
          style: { zIndex: 999999999999999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
        // Toggle back to "Edit" mode
        handleEditToggle("documents");
      }
    } catch (error) {
      console.error("Error updating national ID information:", error);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const getValidationSchema = (section) => {
    /* ---------- 1. Insurance fields ---------- */
    const baseInsuranceSchema = {
      insuranceClass: Yup.string()
        .required("Insurance Class is required")
        .min(2, "Insurance Class must be at least 2 characters")
        .max(30, "Insurance Class must not exceed 30 characters"), // typo in original: 30 vs 10
      insuranceNo: Yup.string()
        .required("Insurance Number is required")
        .matches(
          /^[\u0600-\u06FFA-Za-z0-9]+$/,
          "Only letters and numbers are allowed"
        )
        .min(5, "Insurance Number must be at least 5 characters")
        .max(20, "Insurance Number must not exceed 20 characters")
        .trim(),
      insuranceExpiry: Yup.date()
        .required("Insurance Expiry is required")
        .typeError("Invalid date format"),
    };

    /* ---------- 2. Documents (re‑use your helper) ---------- */
    const documentSchema = {
      insuranceDocFront: documentValidation(), // e.g. file, img, pdf – adjust keys to your form
      insuranceDocBack: documentValidation(),
    };

    /* ---------- 3. Compose schema based on current step ---------- */
    switch (section) {
      case "insurance":
        return Yup.object().shape(baseInsuranceSchema);

      case "documents":
        return Yup.object().shape(documentSchema);

      default:
        // full‑form validation (all steps together)
        return Yup.object().shape({
          ...baseInsuranceSchema,
          ...documentSchema,
        });
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  function handleOpenFileInput() {
    fileInputRef1.current.click();
  }

  function handleOpenFileInput2() {
    fileInputRef2.current.click();
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty values
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const calculateExpiryDays = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    if (isNaN(expiryDate)) return null;
    const diffInMs = expiryDate - new Date();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays > 0 ? `Expiry In ${diffInDays} Days` : "Expired";
  };

  const handleEditToggle = (section, values) => {
    setEditingSections((prev) => {
      const isCurrentlyOpen = prev[section];
      const newState = {
        healthInsurance: false,
        documents: false,
      };

      // If the clicked section was previously closed, open it
      if (!isCurrentlyOpen) {
        newState[section] = true;
      }

      return newState;
    });

    if (values) {
      setInitialValues(values);
    }
  };

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      width: "100%",
      height: "56px",
      padding: "0 6px",
      borderRadius: "8px",
      display: "flex",
      justifyContent: "space-between",
      background: "#f9fafb",
      border: "1px solid #eaecf0",
      fontFamily: "Poppins, sans-serif",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "18px",
      textAlign: "left",
      color: "#111827",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#A0A0A0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#04080b",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isFocused ? "#f3f4f6" : "#fff",
      color: "#04080b",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
  };

  return (
    <>
      <div className="tab-section">
        {/* Iqama Section */}
        <Formik
          enableReinitialize
          initialValues={{
            insuranceClass: healthInsuranceDetails?.insuranceClass || "",
            insuranceNo: healthInsuranceDetails?.insuranceNo || "",
            insuranceExpiry: healthInsuranceDetails?.insuranceExpiry || "",
            // healthCardNo: healthInsuranceDetails?.healthCardNo || "",
            // healthCardExpiry: healthInsuranceDetails?.healthCardExpiry || "",
            insuranceDocFront: healthInsuranceDetails?.frontDocument || "",
            insuranceDocBack: healthInsuranceDetails?.backDocument || "",
          }}
          validationSchema={
            editingSections.healthInsurance
              ? getValidationSchema("insurance")
              : editingSections.documents
              ? getValidationSchema("documents")
              : undefined
          } // Your validation schema
          onSubmit={(values) => {
            if (editingSections.healthInsurance) {
              handleSubmit(values);
            } else if (editingSections.documents) {
              handleUpdateDocuments(values);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, resetForm }) => (
            <>
              <div className="tab-header-container">
                {console.log("values: ", values)}
                <h3 className="tab-heading">{t("Health Insurance")}</h3>
                {currentEmployeeEditing ? (
                  <EditingRestrictionNotice message="Read-only" />
                ) : (
                  !editingSections.healthInsurance && (
                    <div
                      className="tab-header-edit-container"
                      onClick={() =>
                        handleEditToggle("healthInsurance", values)
                      }
                    >
                      <img src={Edit} alt="edit icon" />
                      <span className="edit-txt">{t("Edit")}</span>
                    </div>
                  )
                )}
              </div>
              <Form className="form-section">
                <div className="add-body-content-container-pr">
                  {/* <div className="add-body-content-header">
                    <h3 className="add-body-content-heading">
                      Health Insurance
                    </h3>
                  </div> */}
                  <div className="add-body-content-flex">
                    {/* Insurance Class */}
                    <div className="form-group">
                      <div className="add-body-input-section-full">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="insuranceClass">
                            {t("Health Insurance Class")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {editingSections.healthInsurance ? (
                          <input
                            id="insuranceClass"
                            name="insuranceClass"
                            className="add-input"
                            placeholder={t("Enter Health Insurance Class")}
                            value={values.insuranceClass}
                            onChange={(e) =>
                              setFieldValue("insuranceClass", e.target.value)
                            }
                          />
                        ) : (
                          <div className="display-value">
                            {values.insuranceClass || t("N/A")}
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="insuranceClass"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    <div className="add-body-content-grid">
                      {/* Insurance Number */}
                      <div className="form-group">
                        <div className="add-body-input-section">
                          <div className="add-label-container">
                            <label className="add-label" htmlFor="insuranceNo">
                              {t("Health Insurance Number")}
                            </label>
                            <span className="add-req-mark">*</span>
                          </div>
                          {editingSections.healthInsurance ? (
                            <input
                              id="insuranceNo"
                              name="insuranceNo"
                              className="add-input"
                              placeholder={t("Enter Health Insurance Number")}
                              value={values.insuranceNo}
                              onChange={(e) =>
                                setFieldValue("insuranceNo", e.target.value)
                              }
                            />
                          ) : (
                            <div className="display-value">
                              {values.insuranceNo || t("N/A")}
                            </div>
                          )}
                        </div>
                        <CustomErrorMessage
                          name="insuranceNo"
                          errors={errors}
                          touched={touched}
                        />
                      </div>

                      {/* Insurance Expiry */}
                      <div className="form-group">
                        <div className="add-body-input-section">
                          <div className="add-label-container">
                            <label
                              className="add-label"
                              htmlFor="insuranceExpiry"
                            >
                              {t("Insurance Expiry")}
                            </label>
                            <span className="add-req-mark">*</span>
                          </div>
                          {editingSections.healthInsurance ? (
                            <div className="add-input-wrapper">
                              <input
                                id="insuranceExpiry"
                                type="date"
                                name="insuranceExpiry"
                                className="add-wrap-input"
                                placeholder="DD/MM/YYYY"
                                min={new Date().toISOString().split("T")[0]}
                                value={formatDateToYYYYMMDD(
                                  values.insuranceExpiry
                                )}
                                onChange={(e) =>
                                  setFieldValue(
                                    "insuranceExpiry",
                                    e.target.value
                                  )
                                }
                              />
                              <img
                                src={dateIcon}
                                alt="date icon"
                                className="add-date-input-in"
                              />
                            </div>
                          ) : (
                            <div className="display-value">
                              {formatDateToYYYYMMDD(values.insuranceExpiry) ||
                                t("N/A")}
                            </div>
                          )}
                        </div>
                        <CustomErrorMessage
                          name="insuranceExpiry"
                          errors={errors}
                          touched={touched}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit button for Health Insurance section (only visible in edit mode) */}
                {editingSections.healthInsurance && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          handleEditToggle("healthInsurance");
                        }}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Documents Section Header */}
                <div className="tab-header-container">
                  <h3 className="tab-heading">{t("Documents")}</h3>
                  {currentEmployeeEditing ? (
                    <EditingRestrictionNotice message="View-only" />
                  ) : (
                    !editingSections.documents && (
                      <div
                        className="tab-header-edit-container"
                        onClick={() => handleEditToggle("documents")}
                      >
                        <img src={Edit} alt="edit icon" />
                        <span className="edit-txt">{t("Edit")}</span>
                      </div>
                    )
                  )}
                </div>

                {/* Documents Section Body */}
                {editingSections.documents ? (
                  // Edit Mode
                  <div className="upload-body-tab">
                    {healthInsuranceError && (
                      <div className="details-error">
                        {healthInsuranceError}
                      </div>
                    )}

                    {/* Front Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="insuranceDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames.frontDocument.fileName ||
                                fileNames.frontDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="frontDocument"
                              ref={fileInputRef1}
                              id="insuranceDocFront"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.frontDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "frontDocument",
                                  setFieldValue
                                )
                              } // Pass the document type
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="insuranceDocFront"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="insuranceDocBack"
                            className="upload-label"
                          >
                            {t("Back Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput2}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames.backDocument.fileName ||
                                fileNames.backDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="backDocument"
                              ref={fileInputRef2}
                              id="insuranceDocBack"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.backDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "backDocument",
                                  setFieldValue
                                )
                              } // Pass the document type
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="insuranceDocBack"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                ) : (
                  // Non-Edit Mode
                  <div className="upload-body-tab">
                    {/* Front Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="insuranceDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.insuranceDocFront
                                ? t("Front Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.insuranceDocFront && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.insuranceDocFront,
                                  "Front Document",
                                  "Insurance Front"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="insuranceDocBack"
                            className="upload-label"
                          >
                            {t("Back Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.insuranceDocBack
                                ? t("Back Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.insuranceDocBack && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.insuranceDocBack,
                                  "Back Document",
                                  "Insurance Back"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit button for Documents section (only visible in edit mode) */}
                {editingSections.documents && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => handleEditToggle("documents")}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      </div>

      <DocumentViewer
        isOpen={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={selectedDocument?.url}
        documentType={selectedDocument?.type}
        documentLabel={selectedDocument?.label}
      />
    </>
  );
};

export default HealthInsuranceNew;
